"use client";

import Image from "next/image";
import { ShowWithComedian } from "@punchup/data/legacy";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { useTixologiEvent } from "@punchup/data/legacy/hooks/tixologi";
import { useVenuePage } from "../../../hooks/venuePages/useVenuePage";

interface Props {
  show: ShowWithComedian;
  venuePageId: string;
}

export default function ShowPreviewCard({ show, venuePageId }: Props) {
  const { venuePage } = useVenuePage({ id: venuePageId });
  const venueLogoImg = venuePage?.logo_img ?? null;
  const placeholderBackgroundColor = "rgb(31, 31, 31)";
  const tixologiEventData = useTixologiEvent(
    show.tixologi_event_id ?? undefined
  );
  const event = tixologiEventData.data;

  const hasTixologiImage =
    show.tixologi_event_id &&
    event &&
    typeof event === "object" &&
    "images" in event &&
    event.images.length > 0;

  const imageUrl = hasTixologiImage
    ? event.images[0].image
    : show.poster_img || null;

  const showDatetime = dayjs(show.datetime);
  const todayDatetime = dayjs();

  const dayLabel = todayDatetime.isSame(showDatetime, "day")
    ? "Today"
    : showDatetime.format("dddd");
  const dateLabel = showDatetime.format("MMM D");
  const timeLabel = showDatetime.format("h:mma");
  const dateFormatted = `${dayLabel}, ${dateLabel} • ${timeLabel}`;
  const title = show.title || show.venue || "Untitled Show";

  return (
    <div className="rounded-lg overflow-hidden bg-gray-900 aspect-[16/9] relative">
      {imageUrl ? (
        <Image src={imageUrl} alt={title} fill className="object-cover" />
      ) : venueLogoImg ? (
        <div
          className="w-full h-full flex items-center justify-center p-4"
          style={{
            backgroundColor: placeholderBackgroundColor ?? "rgb(31, 31, 31)",
          }}
        >
          <Image
            src={getCDNImageURL(venueLogoImg)}
            alt="Venue logo"
            width={120}
            height={120}
            className="max-h-[80%] w-auto object-contain"
          />
        </div>
      ) : (
        <div
          className="w-full h-full"
          style={{
            backgroundColor: placeholderBackgroundColor ?? "rgb(31, 31, 31)",
          }}
        />
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
        <div className="text-white text-sm font-medium">{dateFormatted}</div>
        <div className="text-white text-sm font-medium truncate">{title}</div>
      </div>
    </div>
  );
}
